import { useState } from 'react';

import { useDueExpenses, useExpenseHeads, useExpenseSummary, useExpenses } from '@/api/cash';
import { usePayables } from '@/api/outstanding';
import { Button, Card, PageHeading } from '@/components/PageHeading';
import { useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { againLikeLastTime, lastPaidLine } from '@core/cash/expense';
import { dueWords } from '@core/cash/recurring';
import { formatAmount } from '@core/format/money';
import { monthOf } from '@core/cash/spending';
import { can, hasFeature } from '@core/session/capability';
import type { Session } from '@core/session/types';

/**
 * Money out — what the money was for, before anything opens (canvas "Cash & expense flow", board
 * 3). Three kinds, each filed in its own book: an expense, a vendor payment, the owner's drawings.
 *
 * <p>
 * <strong>"Again, like last time"</strong> opens an expense with the head, method and account of the
 * last one under that head (core `againLikeLastTime`, read from the register's first page — nothing
 * new is stored), and a recurring expense that is due opens filled from it. The amount is typed fresh.
 * </p>
 *
 * <p>
 * Only what this person may do is offered: drawings are the company owner's (`OwnersMoney`), a
 * vendor payment needs its own grant, and the tiles need the register.
 * </p>
 */
export function MoneyOutPage({
  session,
  today,
  mayExpense,
  mayPayVendor,
  mayDraw,
  onExpense,
  onDue,
  onPayVendor,
  onDrawings,
  onMove,
  onCount,
  onCancel,
}: {
  session: Session;
  today: string;
  mayExpense: boolean;
  mayPayVendor: boolean;
  mayDraw: boolean;
  /** An expense, filled like the last one under this head when one is given. */
  onExpense: (headId?: number) => void;
  /** A recurring expense that is due, by its id. */
  onDue: (recurringExpenseId: number) => void;
  onPayVendor: () => void;
  onDrawings: () => void;
  onMove: () => void;
  onCount: () => void;
  onCancel: () => void;
}) {
  const branchId = session.branch?.id;
  const reads = mayExpense && can(session, 'Expense:View') ? branchId : undefined;
  const [month] = useState(() => monthOf(today));

  const recent = useExpenses(reads, { pageSize: 25 });
  const heads = useExpenseHeads(reads);
  const due = useDueExpenses(reads);
  const spent = useExpenseSummary(reads, month.from, month.to, reads !== undefined);
  const payables = usePayables(
    branchId,
    mayPayVendor &&
      can(session, 'Outstanding:ViewFinancials') &&
      hasFeature(session, 'CashAndExpense'),
  );

  const tiles = againLikeLastTime(recent.data?.items ?? [], heads.data);
  // Only the occurrence that can be recorded next — a bill two months late is one tile, not two.
  const dueSoon = (due.data ?? []).filter((item) => item.isNext).slice(0, 2);
  const firstChoice = useAutoFocusFirstField<HTMLButtonElement>();

  const kinds = [
    ...(mayExpense
      ? [
          {
            key: 'expense',
            icon: 'icon icon-receipt',
            tile: 'bg-primary',
            title: 'Expense',
            hint: 'Shop spending: rent, electricity, tea, courier, repairs, bank charges.',
            figure: spent.data
              ? `₹${formatAmount(spent.data.total)} this month${
                  (due.data?.length ?? 0) > 0 ? ` · ${due.data?.length} due` : ''
                }`
              : null,
            onChoose: () => onExpense(),
          },
        ]
      : []),
    ...(mayPayVendor
      ? [
          {
            key: 'vendor',
            icon: 'icon icon-hand-coins',
            tile: 'bg-teal',
            title: 'Pay a vendor',
            hint: "A supplier's bill, the launderer or the tailor. Settles what is owed to them.",
            figure:
              payables.data && payables.data.total > 0
                ? `₹${formatAmount(payables.data.total)} owed to ${payables.data.vendors.length} ${
                    payables.data.vendors.length === 1 ? 'vendor' : 'vendors'
                  }`
                : null,
            onChoose: onPayVendor,
          },
        ]
      : []),
    ...(mayDraw
      ? [
          {
            key: 'drawings',
            icon: 'icon icon-arrow-up-right',
            tile: 'bg-indigo',
            title: "Owner's drawings",
            hint: 'The owner takes money home. Not an expense, so it never touches the shop’s spending.',
            figure: 'What it was for is asked',
            onChoose: onDrawings,
          },
        ]
      : []),
  ];

  return (
    <>
      <PageHeading
        eyebrow="Cash & bank"
        title="Money out"
        subtitle="What was the money for? Each one is filed in its own book."
        overlap
        actions={
          <Button shortcut="back" onClick={onCancel}>
            Back
          </Button>
        }
      />

      <section aria-label="What it is" className="mb-4 grid grid-cols-1 gap-4 px-4 md:grid-cols-3">
        {kinds.map((kind, index) => (
          <button
            key={kind.key}
            ref={index === 0 ? firstChoice : undefined}
            type="button"
            onClick={kind.onChoose}
            className={`ar-card flex cursor-pointer flex-col gap-2.5 p-5 text-start hover:shadow-md ${
              index === 0 ? 'border-primary! border-2' : ''
            }`}
          >
            <span
              className={`flex size-12 items-center justify-center rounded-xl ${kind.tile}`}
              aria-hidden="true"
            >
              <i className={`${kind.icon} text-xl text-[#ffffff]`} />
            </span>
            <span className="font-serif text-[22px] font-semibold text-gray-900">{kind.title}</span>
            <span className="text-default text-[13px]">{kind.hint}</span>
            {kind.figure && (
              <span className="text-primary text-[12px] font-semibold">{kind.figure}</span>
            )}
          </button>
        ))}
      </section>

      <div className="grid grid-cols-12 items-start gap-4">
        {(tiles.length > 0 || dueSoon.length > 0) && (
          <Card className="col-span-12 xl:col-span-8">
            <h2 className="mb-0 text-[17px] font-bold text-gray-900">Again, like last time</h2>
            <p className="text-default mt-0.5 mb-3.5 text-[13px]">
              Opens an expense with the head, method and account filled in. The amount is always
              typed fresh.
            </p>
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              {tiles.map((tile) => (
                <button
                  key={tile.headId}
                  type="button"
                  onClick={() => onExpense(tile.headId)}
                  className="border-border-color hover:bg-light flex cursor-pointer items-center gap-3 rounded-xl border bg-white p-3 text-start"
                >
                  <span
                    className="bg-purple-transparent text-purple flex size-[38px] shrink-0 items-center justify-center rounded-[10px] text-[12px] font-bold"
                    aria-hidden="true"
                  >
                    {initialsOf(tile.headName)}
                  </span>
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate text-[14px] font-semibold text-gray-900">
                      {tile.headName}
                    </span>
                    <span className="text-default truncate text-[12px]">
                      {lastPaidLine(tile, '₹')}
                    </span>
                  </span>
                </button>
              ))}
              {dueSoon.map((item) => {
                const when = dueWords(item.dueOn, today);

                return (
                  <button
                    key={`${item.recurringExpenseId}:${item.dueOn}`}
                    type="button"
                    onClick={() => onDue(item.recurringExpenseId)}
                    className="border-warning/30 bg-warning-transparent flex cursor-pointer items-center gap-3 rounded-xl border p-3 text-start"
                  >
                    <span
                      className="text-warning flex size-[38px] shrink-0 items-center justify-center rounded-[10px] bg-white text-[12px] font-bold"
                      aria-hidden="true"
                    >
                      {initialsOf(item.name)}
                    </span>
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate text-[14px] font-semibold text-gray-900">
                        {item.name}
                      </span>
                      <span className="text-warning truncate text-[12px]">
                        ₹{formatAmount(item.typicalAmount)} · {when.text}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </Card>
        )}

        <Card className="col-span-12 xl:col-span-4">
          <h2 className="mb-3 text-[15px] font-bold text-gray-900">Looking for something else?</h2>
          <ul className="flex flex-col gap-3 ps-0">
            <Elsewhere
              icon="icon icon-arrow-right-left"
              title="Cash to the bank, or to petty cash?"
              words="That is Move money. The money stays the shop's."
              link="Move money"
              onOpen={onMove}
            />
            <Elsewhere
              icon="icon icon-undo-2"
              title="A deposit or refund to a customer?"
              words="Give it back from the booking, so it settles there."
            />
            <Elsewhere
              icon="icon icon-clipboard-check"
              title="The drawer is short?"
              words="Count it. The count corrects the book, with a reason."
              link="Count & check"
              onOpen={onCount}
            />
          </ul>
        </Card>
      </div>
    </>
  );
}

function Elsewhere({
  icon,
  title,
  words,
  link,
  onOpen,
}: {
  icon: string;
  title: string;
  words: string;
  link?: string;
  onOpen?: () => void;
}) {
  return (
    <li className="flex list-none items-start gap-2.5">
      <span
        className="bg-light flex size-7 shrink-0 items-center justify-center rounded-lg text-gray-900"
        aria-hidden="true"
      >
        <i className={`${icon} text-[13px]`} />
      </span>
      <span className="text-default text-[13px]">
        <span className="font-semibold text-gray-900">{title}</span> {words}
        {link && onOpen && (
          <>
            {' '}
            <button
              type="button"
              onClick={onOpen}
              className="text-primary cursor-pointer font-semibold hover:underline"
            >
              {link} →
            </button>
          </>
        )}
      </span>
    </li>
  );
}

/** "Tea & snacks" → "TS"; one word → its first two letters. */
function initialsOf(name: string): string {
  const words = name
    .replace(/[^\p{L}\p{N} ]/gu, ' ')
    .split(' ')
    .filter(Boolean);

  return (
    words.length > 1 ? `${words[0]![0]}${words[1]![0]}` : (words[0] ?? '').slice(0, 2)
  ).toUpperCase();
}
